import { useState, type FormEvent } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import StoreNavbar from '@/components/feature/StoreNavbar';
import { lookupOrder, type TrackedOrder } from '@/services/orderTracking';
import OrderDetail from './components/OrderDetail';

type Status = 'idle' | 'loading' | 'notfound' | 'success' | 'error';

export default function OrderTrackingPage() {
  const [searchParams] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState(searchParams.get('pedido') ?? '');
  const [contact, setContact] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [order, setOrder] = useState<TrackedOrder | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const num = orderNumber.trim();
    const info = contact.trim();
    if (!num || !info) {
      setStatus('error');
      setErrorMsg('Ingresa el número de pedido y tu celular o correo.');
      return;
    }
    setStatus('loading');
    setErrorMsg('');
    setOrder(null);
    try {
      const result = await lookupOrder(num, info);
      if (result.found && result.order) {
        setOrder(result.order);
        setStatus('success');
      } else {
        setStatus('notfound');
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg((err as Error)?.message ?? 'No pudimos consultar tu pedido. Intenta de nuevo.');
    }
  };

  const reset = () => {
    setStatus('idle');
    setOrder(null);
    setErrorMsg('');
  };

  return (
    <div className="min-h-screen bg-background-50">
      <StoreNavbar />

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14 w-full">
        {/* Encabezado */}
        <div className="text-center mb-8 md:mb-10">
          <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-truck-line text-accent-600 text-2xl md:text-3xl"></i>
          </div>
          <h1 className="text-2xl md:text-4xl font-bold text-foreground-950 tracking-tight">Mis pedidos</h1>
          <p className="text-sm md:text-base text-foreground-600 mt-2 max-w-md mx-auto">
            Consulta el estado de tu compra con el número de pedido y el celular o correo que usaste.
          </p>
        </div>

        {/* Formulario */}
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl border border-background-200 p-5 md:p-6"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="order-number" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Número de pedido
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                  <i className="ri-hashtag text-foreground-400 text-sm"></i>
                </span>
                <input
                  id="order-number"
                  name="order-number"
                  type="text"
                  value={orderNumber}
                  onChange={(e) => setOrderNumber(e.target.value)}
                  placeholder="Ej: PCP-0101"
                  className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-300 focus:border-accent-400"
                />
              </div>
            </div>
            <div>
              <label htmlFor="order-contact" className="block text-sm font-medium text-foreground-700 mb-1.5">
                Celular o correo
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                  <i className="ri-smartphone-line text-foreground-400 text-sm"></i>
                </span>
                <input
                  id="order-contact"
                  name="order-contact"
                  type="text"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  placeholder="El dato del pedido"
                  className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-300 focus:border-accent-400"
                />
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={status === 'loading'}
            className="mt-4 w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent-500 text-white rounded-full font-semibold text-sm hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {status === 'loading' ? (
              <>
                <i className="ri-loader-4-line animate-spin text-base"></i>
                Consultando...
              </>
            ) : (
              <>
                <i className="ri-search-line text-base"></i>
                Consultar pedido
              </>
            )}
          </button>

          <p className="text-xs text-foreground-400 mt-3 flex items-center gap-1.5">
            <i className="ri-lock-line"></i>
            Usamos estos datos solo para verificar que el pedido es tuyo.
          </p>
        </form>

        {/* Estados */}
        <div className="mt-6">
          {status === 'error' && (
            <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-start gap-3">
              <i className="ri-error-warning-line text-red-500 text-lg mt-0.5"></i>
              <div>
                <p className="text-sm font-semibold text-red-700">No pudimos completar la consulta</p>
                <p className="text-xs text-red-600 mt-0.5">{errorMsg}</p>
              </div>
            </div>
          )}

          {status === 'notfound' && (
            <div className="bg-white rounded-2xl border border-background-200 p-6 text-center">
              <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-file-search-line text-amber-600 text-xl"></i>
              </div>
              <p className="text-sm font-semibold text-foreground-900">No encontramos ese pedido</p>
              <p className="text-xs text-foreground-500 mt-1 max-w-sm mx-auto">
                Revisa que el número de pedido y el celular o correo coincidan con los datos de tu compra.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 mt-4">
                <button
                  type="button"
                  onClick={reset}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full border border-background-300 text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>
                  Intentar de nuevo
                </button>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full text-accent-600 hover:bg-accent-50 transition-colors whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line"></i>
                  Volver a la tienda
                </Link>
              </div>
            </div>
          )}

          {status === 'success' && order && (
            <>
              <OrderDetail order={order} contact={contact} />
              <div className="mt-5 text-center">
                <button
                  type="button"
                  onClick={reset}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-full border border-background-300 text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-search-line"></i>
                  Buscar otro pedido
                </button>
              </div>
            </>
          )}

          {status === 'idle' && (
            <div className="bg-background-100 rounded-2xl border border-background-200 p-5 md:p-6">
              <h2 className="text-sm font-semibold text-foreground-900 mb-3 flex items-center gap-2">
                <i className="ri-lightbulb-line text-accent-600"></i>¿Dónde encuentro mi número de pedido?
              </h2>
              <ul className="space-y-2 text-sm text-foreground-600">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                  Te lo enviamos por correo al confirmar tu compra.
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                  Empieza con <strong className="mx-1">PCP-</strong> seguido de números.
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-accent-500 mt-0.5"></i>
                  También aparece en la página de confirmación del pago.
                </li>
              </ul>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}